import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Register from "./partials/Register";
import Login from "./partials/Login";
import { FormProvider, useForm } from "react-hook-form";
import useResolver from "../../../../hooks/useRevolver";
import {
  loginValidationSchema,
  registerValidationSchema,
} from "./partials/libs/validationSchema";
import useAuth from "../../../../hooks/useAuth";
import useQueryState from "../../../../hooks/useQueryState";

type Props = {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
};

type Mode = "login" | "register";

const AuthDialog = ({ isOpen, setIsOpen }: Props) => {
  const [isOpenToast, setIsOpenToast] = useState(false);
  const { queryState } = useQueryState();
  const [mode, setMode] = useState<Mode>("login");
  const [showPassword, setShowPassword] = useState(false);
  const { postRegister, postLogin } = useAuth();

  const handleClose = () => {
    setIsOpen(false);
    loginForm.reset(defaultLoginForm);
    registerForm.reset(defaultRegisterForm);
    setMode("login");
  };

  const defaultRegisterForm = {
    email: "",
    fullName: "",
    password: "",
    confirmPassword: "",
    affiliation: "",
    targetCountries: "",
  };

  const defaultLoginForm = {
    email: "",
    password: "",
  };

  const loginForm = useForm({
    defaultValues: { ...defaultLoginForm },
    resolver: useResolver(loginValidationSchema),
    reValidateMode: "onChange",
    mode: "all",
  });

  const registerForm = useForm({
    defaultValues: { ...defaultRegisterForm },
    resolver: useResolver(registerValidationSchema),
    reValidateMode: "onChange",
    mode: "all",
  });

  const handleCreateAccount = async () => {
    try {
      await postRegister.mutateAsync({
        ...registerForm.getValues(),
        targetCountries: "",
      });
    } catch (error) {
      console.error("An error occurred during mutation:", error);
    } finally {
      handleClose();
    }
  };

  const handleLogin = async () => {
    try {
      await postLogin.mutateAsync({
        ...loginForm.getValues(),
      });
      setIsOpenToast(true);
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (error) {
      console.error("An error occurred during mutation:", error);
      setIsOpenToast(true);
    }
  };

  useEffect(() => {
    if (mode === "login") {
      loginForm.reset(defaultLoginForm);
    } else {
      registerForm.reset(defaultRegisterForm);
    }
  }, [mode]);

  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 400,
        }}
      >
        <Typography fontWeight={400} variant="h5">
          {mode === "login" ? "Login" : "Create Account"}
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {mode === "login" ? (
          <Box component={FormProvider} {...loginForm}>
            <form
              style={{
                width: "100%",
                height: "100%",
              }}
            >
              <Login showPassword={showPassword} />
            </form>
          </Box>
        ) : (
          <Box component={FormProvider} {...registerForm}>
            <form
              style={{
                width: "100%",
                height: "100%",
              }}
            >
              <Register
                showPassword={showPassword}
                setShowPassword={setShowPassword}
                onBackToLogin={() => setMode("login")}
              />
            </form>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {mode === "login" ? (
          <>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setMode("register")}
            >
              Create Account
            </Button>
            <Button
              fullWidth
              variant="contained"
              onClick={handleLogin}
              disabled={!loginForm.formState.isValid || postLogin.isLoading}
            >
              {postLogin.isLoading ? (
                <CircularProgress sx={{ color: "white" }} size={20} />
              ) : (
                "Login"
              )}
            </Button>
          </>
        ) : (
          <Button
            fullWidth
            variant="contained"
            onClick={handleCreateAccount}
            disabled={!registerForm.formState.isValid || postRegister.isLoading}
          >
            {postRegister.isLoading ? (
              <CircularProgress sx={{ color: "white" }} size={20} />
            ) : (
              "Create Account"
            )}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AuthDialog;
